/**
 * Доменный контракт auto-credit (фаза 3) — зеркало нативной стороны:
 * `tech-drive-super-app/packages/mini-apps/auto-credit/src/shared/bridge-contract.ts`.
 * При изменении контракта на нативной стороне — обновить здесь (и наоборот).
 *
 * Системные действия/события (navigation/auth/ui/http) идут через `bridge`/`http`
 * из vendor; здесь только бизнес-специфика auto-credit.
 *
 * Все хелперы — no-op вне WebView, поэтому их можно звать из общего кода без
 * проверок `isInWebView` на каждом вызове.
 */
import { useEffect, useRef } from 'react';

import { isInWebView } from './env';
import {
  type NativeMessage,
  type UnsubscribeFn,
  type WebMessage,
  createTypedBridge
} from './vendor/web';

// ─── Web → Native (доменные действия) ─────────────────────────────────────────
type OrderSubmitAction = WebMessage<'order.submit', { orderId: string }>;
type OrderCancelAction = WebMessage<'order.cancel', { orderId: string }>;
export type LeasingAction = OrderSubmitAction | OrderCancelAction;

// ─── Native → Web (доменные события) ──────────────────────────────────────────
type OrderStatusChangedEvent = NativeMessage<
  'order.statusChanged',
  { orderId: string; status: string }
>;
export type LeasingEvent = OrderStatusChangedEvent;

export type OrderStatusChangedPayload = OrderStatusChangedEvent['payload'];

const leasingBridge = createTypedBridge<LeasingAction, LeasingEvent>();

/** Сообщить нативе, что пользователь подтвердил заявку (no-op вне WebView). */
export const notifyOrderSubmitted = (orderId: string): void => {
  if (isInWebView()) leasingBridge.send('order.submit', { orderId });
};

/** Сообщить нативе, что пользователь отменил заявку (no-op вне WebView). */
export const notifyOrderCancelled = (orderId: string): void => {
  if (isInWebView()) leasingBridge.send('order.cancel', { orderId });
};

/**
 * Подписка на изменение статуса заявки от нативы (no-op вне WebView).
 * @returns функция отписки.
 */
export const onOrderStatusChanged = (
  handler: (payload: OrderStatusChangedPayload) => void
): UnsubscribeFn => {
  if (!isInWebView()) return () => {};
  return leasingBridge.on('order.statusChanged', handler);
};

/**
 * React-хук подписки на статус заявки. Подписывается один раз; всегда зовёт
 * актуальный `handler` через ref, поэтому его можно передавать инлайном.
 */
export const useOrderStatusChanged = (
  handler: (payload: OrderStatusChangedPayload) => void
): void => {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    return onOrderStatusChanged((payload) => handlerRef.current(payload));
  }, []);
};
